import { env } from "@/config/env"
import { HttpClient } from "@/shared/lib/http-client"

import type { SoulmateInput } from "../types"

const RECEIVED_SOULMATE_REPORT_KEY = "RECEIVED_SOULMATE_REPORT"

function alreadyRequested(key: string): boolean {
  try {
    return window.sessionStorage.getItem(RECEIVED_SOULMATE_REPORT_KEY) === key
  } catch {
    return false
  }
}

function markRequested(key: string): void {
  try {
    window.sessionStorage.setItem(RECEIVED_SOULMATE_REPORT_KEY, key)
  } catch {
    /* ignore */
  }
}

class SoulmateReportService extends HttpClient {
  /** Public (quiz / additional-offers): pre-account soulmate report from a raw quiz snapshot. */
  async sendPublic(params: {
    user: SoulmateInput
    email: string
    subscriptionId: string
  }): Promise<void> {
    const email = params.email.toLowerCase()
    if (alreadyRequested(`soulmate:${email}`)) return

    await this.post("/v1/reports/soulmate", {
      subscriptionId: params.subscriptionId,
      email,
      user: params.user,
    })

    markRequested(`soulmate:${email}`)
  }

  /** Authed (webapp): backend derives the profile from the Firebase token. */
  async sendForMe(params: { subscriptionId: string; token: string }): Promise<void> {
    const key = `me:soulmate:${params.subscriptionId}`
    if (alreadyRequested(key)) return

    await this.post(
      "/v1/reports/me/soulmate",
      { subscriptionId: params.subscriptionId },
      { headers: { Authorization: `Bearer ${params.token}` } },
    )

    markRequested(key)
  }
}

const soulmateReportService = new SoulmateReportService({ baseURL: env.VITE_API_URL })

/**
 * Quiz / additional-offers (pre-account): request the soulmate report via the
 * public `POST /v1/reports/soulmate` with a raw quiz snapshot. Best-effort.
 */
export async function requestSoulmateReport(params: {
  user: SoulmateInput
  email: string
  subscriptionId: string
}): Promise<void> {
  if (!env.VITE_API_URL) return
  await soulmateReportService.sendPublic(params)
}

/**
 * Webapp: request the soulmate report for the signed-in user via the authed
 * `POST /v1/reports/me/soulmate` (subscription id only; backend reads the profile
 * and verifies the subscription from the token). Best-effort.
 */
export async function requestMySoulmateReport(params: {
  subscriptionId: string
  token: string
}): Promise<void> {
  if (!env.VITE_API_URL) return
  await soulmateReportService.sendForMe(params)
}
